export const key = value => String(value || '').trim().toLowerCase().replace(/ß/g, 'ss').replace(/[^a-z0-9äöü]/g, '');
export const list = value => value == null || value === '' ? [] : Array.isArray(value) ? value : [value];
export function canonical(field, value) {
  let s = String(value || '').trim(), k = key(s);
  if (!s) return '';
  if (field === 'dim') {
    if (/onoff|nichtdimmbar|^nein$/.test(k)) return 'On/Off';
    if (/phase|triac/.test(k)) return 'Phasendimmung';
    if (/dali/.test(k)) return 'DALI';
    if (/casambi/.test(k)) return 'Casambi';
    if (/push|tast/.test(k)) return 'Push-Dim';
    if (/ohnedriver|ohnetreiber/.test(k)) return 'Ohne Treiber';
  }
  if (field === 'kelvin') {
    if (/dimtowarm|dim2warm|dtw/.test(k)) return 'Dim-to-Warm';
    if (/tunable|dynamicwhite/.test(k)) return 'Tunable White';
    const m = s.match(/(\d{4})\s*(?:K|Kelvin)/i); if (m) return `${m[1]}K`;
  }
  if (field === 'ip') { const m = s.match(/IP\s*(\d{2})/i); if (m) return `IP${m[1]}`; }
  if (field === 'color') {
    s=s.toLowerCase().replace(/weiss/g,'weiß').replace(/^alu$/,'aluminium').replace(/schwarztaupe/g,'schwarz / taupe').replace(/goldmatt/g,'gold matt').replace(/chrom glanz/g,'chrom glänzend').replace(/\brose\b/g,'rosé').replace(/\s*[-/]\s*/g,' / ');
    return s.charAt(0).toUpperCase()+s.slice(1);
  }
  if (field === 'beam') return s.replace(/\s*(grad|°)/ig,'°');
  return s;
}
function readSingle(text, regex, convert = m => m[1]) {
  const values = [...text.matchAll(regex)].map(convert);
  const unique = [...new Set(values.filter(v=>v!==null))];
  return unique.length === 1 ? unique[0] : null;
}
function fallback(p) {
  const text = p.description || '', m = p.legacy || {};
  const mm = x => Number(x[1].replace(',','.')) * (x[2].toLowerCase()==='cm'?10:1);
  const b = {
    ip: m.ip ? canonical('ip',m.ip) : readSingle(text, /Schutzart\s*:?\s*(IP\s*\d{2})/ig, x=>canonical('ip',x[1])),
    depth: readSingle(text,/Einbautiefe?\s*:?\s*(?:mind?\.?\s*|min\.?\s*)?(\d+(?:[.,]\d+)?)\s*(mm|cm)/ig,mm),
    cut: readSingle(text,/(?:Deckenausschnitt|Bohrloch)\s*:?\s*(?:Durchmesser\s*|Ø\s*)?(\d+(?:[.,]\d+)?)\s*(mm|cm)/ig,x=>`Ø${mm(x)} mm`),
    beam: readSingle(text,/Abstrahlwinkel\s*:?\s*(\d+)\s*(?:°|Grad)/ig,x=>`${x[1]}°`),
    lumen: readSingle(text,/(?:Lumen|Lichtstrom)\s*:\s*(\d+)\s*(?:lm)?/ig,x=>Number(x[1]))
  };
  if (!b.depth && m.depth) b.depth=readSingle(m.depth,/(\d+(?:[.,]\d+)?)\s*(mm|cm)/ig,mm);
  if (!b.cut && m.cut) b.cut=readSingle(m.cut,/(\d+(?:[.,]\d+)?)\s*(mm|cm)/ig,x=>`Ø${mm(x)} mm`);
  if (m.color) b.color=canonical('color',m.color);
  if (m.kelvin) b.kelvin=[canonical('kelvin',m.kelvin)];
  if (m.dim) b.dim=canonical('dim',m.dim);
  if (!b.dim) b.dim=readSingle(text,/(?:Dimmart|Dimmbar)\s*:\s*((?:nicht dimmbar|nein|on\s*\/\s*off|Phasendimmung|DALI|Casambi))/ig,x=>canonical('dim',x[1]));
  if (!b.kelvin) {
    const k=readSingle(text,/(?:Farbtemperatur|Lichtfarbe)\s*:\s*(\d{4})\s*(?:Kelvin|K)\b/ig,x=>`${x[1]}K`);
    if(k) b.kelvin=[k];
  }
  const empty=/ohne\s+(?:LED[ -]?)?Leuchtmittel/i.test(text);
  b.lamp=empty ? (/GU10/.test(text)?'GU10':'Ohne Leuchtmittel') : (/fest verbaut|LED.Modul/i.test(text)?'LED-Modul':null);
  if(empty) {b.dim='Je nach Leuchtmittel';b.kelvin=['Je nach Leuchtmittel'];b.lumen=null;b.depth=null;}
  return b;
}
export function resolveProduct(p) {
  const facts=p.facts || {}, base=facts.version ? facts.base : fallback(p);
  return {...p,variants:p.variants.map(v=>{
    const opts=Object.fromEntries(v.options.map(o=>[key(o.name),o.value]));
    const light=Object.entries(opts).find(([k])=>['lichtfarbe','farbtemperatur'].includes(k))?.[1];
    const props={...base};
    for(const rule of facts.rules || []) {
      if(Object.entries(rule.when).every(([k,val])=>key(k==='light'?light:opts[key(k)])===key(val))) Object.assign(props,rule.set);
    }
    for(const [field,names] of Object.entries({dim:['dimmart','dimmung','steuerung'],color:['farbe','farben','oberfläche','gehäusefarbe'],kelvin:['lichtfarbe','farbtemperatur'],ip:['schutzart'],beam:['abstrahlwinkel']})) {
      const entry=names.find(n=>opts[key(n)]);
      if(entry) props[field]=canonical(field,opts[key(entry)]);
    }
    const driver=Object.values(opts).find(v=>/driver|treiber/i.test(v));
    if(driver) props.dim=canonical('dim',driver);
    if(facts.combineColor) props.color=facts.combineColor.map(n=>canonical('color',opts[key(n)])).join(' / ');
    if(facts.matte && props.color) props.color=props.color+' matt';
    for(const field of ['dim','color','kelvin','ip','beam','lamp','cut']) props[field]=list(props[field]).map(x=>canonical(field,x));
    props.vendor=[p.vendor];
    props.stock=v.available ? (v.quantity>0 ? ['Sofort lieferbar','Bestellbar'] : ['Bestellbar']) : [];
    return {...v,props};
  }).filter(v=>!v.props.exclude)};
}
export function cutMatches(actual, wanted) {
  if(actual===wanted) return true;
  const a=actual.match(/^Ø(\d+(?:[.,]\d+)?)[–-](\d+(?:[.,]\d+)?) mm$/), b=wanted.match(/^Ø(\d+(?:[.,]\d+)?) mm$/);
  return !!(a&&b&&Number(b[1])>=Number(a[1])&&Number(b[1])<=Number(a[2]));
}
export function variantMatches(v, selected={}, limits={}, except='') {
  for(const [field,values] of Object.entries(selected)) {
    if(field===except||!values.length) continue;
    if(!values.some(w=>list(v.props[field]).some(a=>field==='cut'?cutMatches(a,w):a===w))) return false;
  }
  const checks=[['priceMin',v.price,'min'],['priceMax',v.price,'max'],['depth',v.props.depth,'max'],['lumenMin',v.props.lumen,'min'],['lumenMax',v.props.lumen,'max']];
  return checks.every(([k,val,op])=>limits[k]==null||limits[k]===''||(val!=null&&(op==='min'?val>=Number(limits[k]):val<=Number(limits[k]))));
}
export function productMatches(p,type,selected,limits,except='') {
  return (!type||p.types.includes(type)) && p.variants.some(v=>variantMatches(v,selected,limits,except));
}
